import {
  Editable,
  EditableInput,
  EditablePreview,
  Text,
} from '@chakra-ui/react'
import { useUpdateCircleMemberMutation } from '@gql'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  circleMemberId: string
  focus?: string | null
  canEdit: boolean
}

// Free-text "focus" of a member on a circle (e.g. Aziz -> South-West). Purely
// cosmetic: no governance, no link to projects/threads/next steps.
export default function CircleMemberFocusInput({
  circleMemberId,
  focus,
  canEdit,
}: Props) {
  const { t } = useTranslation()
  const [updateCircleMember] = useUpdateCircleMemberMutation()

  const handleSubmit = useCallback(
    (nextValue: string) => {
      const trimmed = nextValue.trim()
      if ((focus ?? '') === trimmed) return
      updateCircleMember({
        variables: { id: circleMemberId, values: { focus: trimmed || null } },
      })
    },
    [circleMemberId, focus, updateCircleMember]
  )

  const hasFocus = !!focus

  // Read-only: show the focus as grey italic text, nothing when empty.
  if (!canEdit) {
    if (!hasFocus) return null
    return (
      <Text as="span" fontStyle="italic" fontSize="sm" color="gray.500">
        {focus}
      </Text>
    )
  }

  return (
    <Editable
      // Remount when the focus value changes (uncontrolled defaultValue).
      key={`${circleMemberId}:${focus ?? ''}`}
      defaultValue={focus ?? ''}
      placeholder={t('CircleMemberFocus.placeholder')}
      onSubmit={handleSubmit}
      submitOnBlur
      fontStyle="italic"
      fontSize="sm"
      color="gray.500"
      sx={
        hasFocus
          ? undefined
          : {
              // Empty state: the "+ focus" trigger stays discreet — hidden until
              // the member row is hovered on pointer devices, always shown on
              // touch (where there is no hover).
              '@media (hover: hover)': {
                opacity: 0,
                transition: 'opacity 0.15s',
                '[role="group"]:hover &': { opacity: 1 },
                '&:focus-within': { opacity: 1 },
              },
            }
      }
    >
      <EditablePreview cursor="pointer" />
      <EditableInput />
    </Editable>
  )
}
